<script>
import Layout from "../../layouts/main.vue";
import PageHeader from "@/components/page-header";
import appConfig from "../../../app.config";
import axios from 'axios';
import DataTable from 'datatables.net-vue3';
import Swal from 'sweetalert2';
import { Modal } from "bootstrap";
import { usePermissions } from '@/composables/usePermissions';

export default {
  page: {
    title: "Referral Codes",
    meta: [{ name: "description", content: appConfig.description }],
  },
  data() {
    return {
      role: localStorage.getItem('role'),
      title: "Referral Codes",
      items: [
        { text: "CRM", href: "/" },
        { text: "Referral Codes", active: true },
      ],
      referralList: [],
      DetailReferral: {},
      groups: [],
      tableKey: 0,
      type: 'user_referral',
      code: '',
      email: '',
      group_id: '',
      newGroupName: '',
      newGroupDescription: '',
      importData: '',
      selectedId: null,
      loadingList: true,
      loadingDetail: false,
    };
  },
  setup() {
    const {
      canReadReferralCode,
      canUpdateReferralCode,
      canCreateReferralCode
    } = usePermissions();
    return { canReadReferralCode, canUpdateReferralCode, canCreateReferralCode };
  },
  methods: {
    // Improved Bootstrap Modal Close with proper cleanup
    closeModal(modalId) {
      return new Promise((resolve) => {
        const modalEl = document.getElementById(modalId);
        if (!modalEl) {
          // Still cleanup even if modal doesn't exist
          this.cleanupModalBackdrops();
          resolve();
          return;
        }

        let modal = Modal.getInstance(modalEl);
        if (!modal) {
          modal = new Modal(modalEl);
        }

        // Flag to prevent multiple cleanups
        let isCleaned = false;

        const cleanup = () => {
          if (isCleaned) return;
          isCleaned = true;
          
          // Force cleanup of all modal artifacts
          this.cleanupModalBackdrops();
          
          // Remove any inline styles
          modalEl.style.display = '';
          modalEl.classList.remove('show');
          
          // Small delay to ensure DOM stability
          setTimeout(resolve, 150);
        };

        // Listen for the hidden event
        modalEl.addEventListener('hidden.bs.modal', cleanup, { once: true });

        // Start hiding the modal
        try {
          modal.hide();
        } catch (error) {
          // If hide fails, force cleanup
          cleanup();
        }

        // Safety timeout in case event doesn't fire
        setTimeout(() => {
          if (!isCleaned) {
            cleanup();
          }
        }, 600);
      });
    },

    // Centralized cleanup function
    cleanupModalBackdrops() {
      // Remove modal-open class from body
      document.body.classList.remove('modal-open');
      
      // Remove padding-right style
      document.body.style.removeProperty('padding-right');
      
      // Remove all modal backdrops
      document.querySelectorAll('.modal-backdrop').forEach(el => {
        el.remove();
      });
      
      // Remove any lingering fade classes
      document.querySelectorAll('.modal.fade').forEach(el => {
        el.classList.remove('show');
        el.style.display = '';
      });
    },

    resetReferralForm() {
      this.type = 'user_referral';
      this.code = '';
      this.email = '';
      this.group_id = '';
    },

    async refreshData() {
      this.loadingList = true;
      try {
        axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt');
        
        const [referralsRes, groupsRes] = await Promise.all([
          axios.get(process.env.VUE_APP_API_BASEURL + 'referral/list'),
          axios.get(process.env.VUE_APP_API_BASEURL + 'referral/groups')
        ]);

        this.referralList = referralsRes.data;
        this.groups = groupsRes.data;
        this.tableKey++;
      } catch (error) {
        console.error(error);
        Swal.fire({
          title: 'Error',
          text: 'Failed to load referral data',
          icon: 'error'
        });
      } finally {
        this.loadingList = false;
      }
    },

    GetDetailReferral(id) {
      this.loadingDetail = true;
      this.DetailReferral = this.referralList.find(item => item.id === id) || {};
      this.loadingDetail = false;
    },

    setActionId(id) {
      this.selectedId = id;
    },

    async toggleStatus(activate) {
      if (!this.selectedId) {
        Swal.fire({
          title: 'Error',
          text: 'No referral code selected',
          icon: 'error'
        });
        return;
      }

      const modalId = activate ? 'activateModal' : 'deleteReferral';
      const actionText = activate ? 'activated' : 'deactivated';

      try {
        // Close the modal first
        await this.closeModal(modalId);

        // Small delay to ensure modal is fully closed
        await new Promise(resolve => setTimeout(resolve, 200));

        // Make API call
        axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt');
        const response = await axios.patch(
          process.env.VUE_APP_API_BASEURL + `referral/${this.selectedId}/status`,
          { is_active: activate }
        );

        // Show success message
        await Swal.fire({
          title: 'Success',
          text: response.data.msj || `Referral code ${actionText} successfully`,
          icon: 'success',
          timer: 1500,
          showConfirmButton: false,
          allowOutsideClick: false
        });

        // Reset and refresh
        this.selectedId = null;
        await this.refreshData();

      } catch (error) {
        // Ensure cleanup even on error
        this.cleanupModalBackdrops();
        
        Swal.fire({
          title: 'Error',
          text: error.response?.data?.message || `Failed to ${actionText} referral code`,
          icon: 'error'
        });
      }
    },

    confirmDeactivate() { 
      this.toggleStatus(false); 
    },

    confirmActivate() { 
      this.toggleStatus(true); 
    },

    async AddNewReferral(e) {
      e.preventDefault();
      
      // Validation
      if (!this.code || this.code.trim() === '') {
        Swal.fire({
          title: 'Validation Error',
          text: 'Please enter a referral code',
          icon: 'warning'
        });
        return;
      }

      const posting_ = {
        type: this.type,
        code: this.code.trim(),
        email: this.type === 'user_referral' ? this.email : null,
        group_id: this.group_id || null,
      };

      try {
        axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt');
        await axios.post(process.env.VUE_APP_API_BASEURL + 'referral/create', posting_);

        // Close modal and reset form
        await this.closeModal('createReferralModal');
        this.resetReferralForm();
        
        // Reset form element if exists
        const form = document.getElementById("AddNewReferral");
        if (form) form.reset();

        // Show success
        await Swal.fire({
          title: 'Success',
          text: 'Referral code created successfully',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false,
          allowOutsideClick: false
        });

        // Refresh data
        await this.refreshData();

      } catch (error) {
        this.cleanupModalBackdrops();
        Swal.fire({ 
          title: 'Error', 
          text: error.response?.data?.message || 'Failed to create referral code', 
          icon: 'error' 
        });
      }
    },

    async createGroup(e) {
      e.preventDefault();
      
      if (!this.newGroupName.trim()) {
        Swal.fire({
          title: 'Validation Error',
          text: 'Please enter a group name',
          icon: 'warning'
        });
        return;
      }

      try {
        axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt');
        const res = await axios.post(process.env.VUE_APP_API_BASEURL + 'referral/groups', {
          name: this.newGroupName.trim(),
          description: this.newGroupDescription || ''
        });

        await this.closeModal('createGroupModal');
        
        Swal.fire({ 
          title: 'Success', 
          text: res.data.msj || 'Group created successfully', 
          icon: 'success', 
          timer: 1500,
          showConfirmButton: false,
          allowOutsideClick: false
        });

        this.newGroupName = '';
        this.newGroupDescription = '';
        await this.refreshData();

      } catch (error) {
        this.cleanupModalBackdrops();
        Swal.fire({ 
          title: 'Error', 
          text: error.response?.data?.message || 'Failed to create group', 
          icon: 'error' 
        });
      }
    },

    async importReferralCodes(e) {
      e.preventDefault();
      
      if (!this.importData.trim()) {
        Swal.fire({
          title: 'Validation Error',
          text: 'Please enter referral codes to import',
          icon: 'warning'
        });
        return;
      }

      const rows = this.importData.trim().split('\n');
      const codes = rows
        .map(row => {
          const parts = row.split(',').map(s => s.trim());
          return {
            code: parts[0] || '',
            email: parts[1] || '',
            group: parts[2] || ''
          };
        })
        .filter(item => item.code);

      if (codes.length === 0) {
        Swal.fire({
          title: 'Validation Error',
          text: 'No valid codes found in the import data',
          icon: 'warning'
        });
        return;
      }

      try {
        axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt');
        const response = await axios.post(process.env.VUE_APP_API_BASEURL + 'referral/import', { codes });

        let msg = response.data.msj || 'Import successful';
        if (response.data.warnings && response.data.warnings.length > 0) {
          msg += '\n\nWarnings:\n' + response.data.warnings.join('\n');
        }

        await this.closeModal('importModal');
        
        Swal.fire({ 
          title: 'Success', 
          text: msg, 
          icon: 'success', 
          timer: 2000,
          showConfirmButton: false,
          allowOutsideClick: false
        });

        this.importData = '';
        await this.refreshData();

      } catch (error) {
        this.cleanupModalBackdrops();
        Swal.fire({ 
          title: 'Import Failed', 
          text: error.response?.data?.message || 'Unknown error occurred', 
          icon: 'error' 
        });
      }
    },

    // Handle modal close events from Bootstrap
    handleModalHidden() {
      this.cleanupModalBackdrops();
    }
  },
  components: { Layout, PageHeader, DataTable },
  mounted() {
    if (this.canReadReferralCode) {
      this.refreshData();
    } else {
      Swal.fire({
        title: 'Access Denied',
        text: 'You are not authorized to view the requested page!',
        icon: 'error'
      });
    }

    // Add global event listeners for modal hidden events
    document.addEventListener('hidden.bs.modal', this.handleModalHidden);
  },
  beforeUnmount() {
    // Clean up event listeners
    document.removeEventListener('hidden.bs.modal', this.handleModalHidden);
    this.cleanupModalBackdrops();
  },
};
require("@/assets/css/datatable.css");
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="d-flex justify-content-end mb-3 gap-2 flex-wrap">
      <button 
        class="btn btn-primary" 
        data-bs-toggle="modal" 
        data-bs-target="#createReferralModal" 
        v-if="canCreateReferralCode"
      >
        <i class="ri-add-line align-middle me-1"></i> Create New Referral Code
      </button>
      <button 
        class="btn btn-info" 
        data-bs-toggle="modal" 
        data-bs-target="#createGroupModal" 
        v-if="canCreateReferralCode"
      >
        <i class="ri-group-line align-middle me-1"></i> Create Group
      </button>
      <button 
        class="btn btn-success" 
        data-bs-toggle="modal" 
        data-bs-target="#importModal" 
        v-if="canCreateReferralCode"
      >
        <i class="ri-upload-2-line align-middle me-1"></i> Import Referral Codes
      </button>
    </div>

    <div class="card">
      <div class="card-body" v-if="canReadReferralCode">
        <div class="live-preview">
          <div class="row align-items-center g-12">
            <div class="col-lg-12">
              <div v-if="loadingList" class="text-center py-4">
                <div class="spinner-border text-success" role="status">
                  <span class="sr-only">Loading...</span>
                </div>
                <p class="mt-2 text-muted">Loading referral codes...</p>
              </div>
              <div v-else>
                <div class="table-responsive">
                  <DataTable 
                    :key="tableKey" 
                    class="table table-striped table-nowrap mb-0"
                    :options="{
                      pageLength: 25,
                      order: [[0, 'asc']],
                      language: {
                        emptyTable: 'No referral codes found'
                      }
                    }"
                  >
                    <thead class="table-secondary">
                      <tr>
                        <th style="width: 60px;">No.</th>
                        <th>Code</th>
                        <th>Group</th>
                        <th>Type</th>
                        <th>Email</th>
                        <th>Status</th>
                        <th style="width: 100px;">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, index) in referralList" :key="item.id || index">
                        <td>{{ index + 1 }}</td>
                        <td><strong>{{ item.code }}</strong></td>
                        <td>{{ item.group?.name || item.group_name || '-' }}</td>
                        <td>
                          <span class="badge" :class="item.type === 'promo' ? 'bg-info' : 'bg-primary'">
                            {{ item.type === 'promo' ? 'Promo' : 'User Referral' }}
                          </span>
                        </td>
                        <td>{{ item.email || '-' }}</td>
                        <td>
                          <span class="badge" :class="item.is_active ? 'bg-success' : 'bg-danger'">
                            {{ item.is_active ? 'Active' : 'Inactive' }}
                          </span>
                        </td>
                        <td>
                          <div class="d-flex gap-2">
                            <i 
                              @click="GetDetailReferral(item.id)" 
                              class="ri-eye-fill text-primary" 
                              data-bs-toggle="modal" 
                              data-bs-target="#detailModal" 
                              style="cursor:pointer; font-size: 1.1rem;"
                              title="View Details"
                            ></i>
                            <i 
                              v-if="canUpdateReferralCode && item.is_active" 
                              @click="setActionId(item.id)" 
                              class="ri-delete-bin-5-fill text-danger" 
                              data-bs-toggle="modal" 
                              data-bs-target="#deleteReferral" 
                              style="cursor:pointer; font-size: 1.1rem;" 
                              title="Deactivate"
                            ></i>
                            <i 
                              v-if="canUpdateReferralCode && !item.is_active" 
                              @click="setActionId(item.id)" 
                              class="ri-check-line text-success" 
                              data-bs-toggle="modal" 
                              data-bs-target="#activateModal" 
                              style="cursor:pointer; font-size: 1.1rem;" 
                              title="Activate"
                            ></i>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </DataTable>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="text-center text-muted p-4">
        <i class="ri-lock-line fs-2 d-block mb-2"></i>
        You do not have permission to view Referral Codes.
      </div>
    </div>

    <!-- Create Referral Modal -->
    <div class="modal fade" id="createReferralModal" tabindex="-1" aria-labelledby="createReferralModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="createReferralModalLabel">Create New Referral Code</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <form id="AddNewReferral" @submit.prevent="AddNewReferral">
            <div class="modal-body">
              <div class="mb-3">
                <label class="form-label">Code Type</label>
                <select v-model="type" class="form-select" required>
                  <option value="user_referral">User Referral</option>
                  <option value="promo">Promo</option>
                </select>
              </div>
              <div class="mb-3">
                <label class="form-label">Referral Code <span class="text-danger">*</span></label>
                <input 
                  type="text" 
                  v-model="code" 
                  class="form-control" 
                  placeholder="Enter referral code"
                  required
                >
              </div>
              <div class="mb-3" v-if="type === 'user_referral'">
                <label class="form-label">Email <span class="text-danger">*</span></label>
                <input 
                  type="email" 
                  v-model="email" 
                  class="form-control" 
                  placeholder="Enter user email"
                  required
                >
              </div>
              <div class="mb-3">
                <label class="form-label">Group (Optional)</label>
                <select v-model="group_id" class="form-select">
                  <option value="">No Group</option>
                  <option v-for="group in groups" :key="group.id" :value="group.id">
                    {{ group.name }}
                  </option>
                </select>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
              <button type="submit" class="btn btn-primary">Create</button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Create Group Modal -->
    <div class="modal fade" id="createGroupModal" tabindex="-1" aria-labelledby="createGroupModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="createGroupModalLabel">Create Group</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <form @submit.prevent="createGroup">
            <div class="modal-body">
              <div class="mb-3">
                <label class="form-label">Group Name <span class="text-danger">*</span></label>
                <input 
                  type="text" 
                  v-model="newGroupName" 
                  class="form-control" 
                  placeholder="Enter group name"
                  required
                >
              </div>
              <div class="mb-3">
                <label class="form-label">Description</label>
                <textarea 
                  v-model="newGroupDescription" 
                  class="form-control" 
                  rows="3"
                  placeholder="Enter group description (optional)"
                ></textarea>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
              <button type="submit" class="btn btn-info">Create Group</button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Import Modal -->
    <div class="modal fade" id="importModal" tabindex="-1" aria-labelledby="importModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="importModalLabel">Import Referral Codes</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <form @submit.prevent="importReferralCodes">
            <div class="modal-body">
              <div class="mb-3">
                <label class="form-label">Enter Referral Codes</label>
                <textarea 
                  v-model="importData" 
                  class="form-control" 
                  rows="8"
                  placeholder="Enter one referral code per line&#10;Format: code,email,group&#10;Example:&#10;ABC123,user@example.com,Group1&#10;DEF456,another@example.com,Group2&#10;GHI789,,PromoGroup"
                ></textarea>
                <small class="text-muted d-block mt-2">
                  <i class="ri-info-line me-1"></i> Format: <code>code,email,group</code> (email and group are optional)
                </small>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
              <button type="submit" class="btn btn-success">Import</button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Detail Modal -->
    <div class="modal fade" id="detailModal" tabindex="-1" aria-labelledby="detailModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="detailModalLabel">Referral Code Details</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="loadingDetail" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="sr-only">Loading...</span>
              </div>
            </div>
            <div v-else>
              <dl class="row mb-0">
                <dt class="col-sm-4">Code</dt>
                <dd class="col-sm-8"><strong>{{ DetailReferral.code || '-' }}</strong></dd>

                <dt class="col-sm-4">Type</dt>
                <dd class="col-sm-8">
                  <span class="badge" :class="DetailReferral.type === 'promo' ? 'bg-info' : 'bg-primary'">
                    {{ DetailReferral.type === 'promo' ? 'Promo' : 'User Referral' }}
                  </span>
                </dd>

                <dt class="col-sm-4">Email</dt>
                <dd class="col-sm-8">{{ DetailReferral.email || '-' }}</dd>

                <dt class="col-sm-4">Group</dt>
                <dd class="col-sm-8">{{ DetailReferral.group?.name || DetailReferral.group_name || '-' }}</dd>

                <dt class="col-sm-4">Status</dt>
                <dd class="col-sm-8">
                  <span class="badge" :class="DetailReferral.is_active ? 'bg-success' : 'bg-danger'">
                    {{ DetailReferral.is_active ? 'Active' : 'Inactive' }}
                  </span>
                </dd>

                <dt class="col-sm-4">Created At</dt>
                <dd class="col-sm-8">{{ DetailReferral.created_at ? new Date(DetailReferral.created_at).toLocaleString() : '-' }}</dd>

                <dt class="col-sm-4">Updated At</dt>
                <dd class="col-sm-8">{{ DetailReferral.updated_at ? new Date(DetailReferral.updated_at).toLocaleString() : '-' }}</dd>
              </dl>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Deactivate Modal -->
    <div class="modal fade" id="deleteReferral" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-body text-center p-5">
            <form @submit.prevent="confirmDeactivate">
              <div class="mb-4">
                <i class="ri-error-warning-line text-danger" style="font-size: 4rem;"></i>
              </div>
              <h4 class="mb-3 text-danger">Deactivate Referral Code?</h4>
              <p class="text-muted">This will set the status to <strong>Inactive</strong>. The code will no longer be usable.</p>
              <div class="hstack gap-2 justify-content-center mt-4">
                <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancel</button>
                <button type="submit" class="btn btn-danger">Deactivate</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Activate Modal -->
    <div class="modal fade" id="activateModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-body text-center p-5">
            <form @submit.prevent="confirmActivate">
              <div class="mb-4">
                <i class="ri-check-double-line text-success" style="font-size: 4rem;"></i>
              </div>
              <h4 class="mb-3 text-success">Activate Referral Code?</h4>
              <p class="text-muted">The code will become <strong>Active</strong> and usable again.</p>
              <div class="hstack gap-2 justify-content-center mt-4">
                <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancel</button>
                <button type="submit" class="btn btn-success">Activate</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
/* Optional custom styles */
.badge {
  font-size: 0.75rem;
  padding: 0.35rem 0.65rem;
}

.table td {
  vertical-align: middle;
}

.hstack {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
}

.gap-2 {
  gap: 0.5rem;
}

/* Ensure proper spacing for action icons */
.ri-eye-fill,
.ri-delete-bin-5-fill,
.ri-check-line {
  transition: transform 0.2s ease;
}

.ri-eye-fill:hover,
.ri-delete-bin-5-fill:hover,
.ri-check-line:hover {
  transform: scale(1.2);
}

/* DataTable custom styling */
.table-responsive {
  overflow-x: auto;
}

@media (max-width: 768px) {
  .d-flex.justify-content-end {
    flex-direction: column;
    align-items: stretch;
  }
  
  .d-flex.justify-content-end .btn {
    margin-bottom: 0.5rem;
  }
}
</style>